import React from "react";

import { InboxOutlined } from "@ant-design/icons";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { Button, Alert } from "@impero-com/ui";
import { Upload } from "antd";
import { UploadFile } from "antd/es/upload/interface";

import {
    ProcessFileResult,
    ProcessorStatus,
} from "../hooks/useSpreadsheetProcessor";
import { BulkAssignStep } from "../stepsContainer";

import * as theme from "./theme.css";
import { ValidationErrors } from "./validationErrors";

const { Dragger } = Upload;

interface UploadAssignmentsStepProps {
    onStepChange: (step: BulkAssignStep) => void;
    onErrorChange: (hasError: boolean) => void;
    status: ProcessorStatus;
    processFile: (file: File) => Promise<ProcessFileResult>;
    resetData: () => void;
}

export function UploadAssignmentsStep({
    onStepChange,
    onErrorChange,
    status,
    processFile,
    resetData,
}: UploadAssignmentsStepProps) {
    const { t } = useTranslation();
    const [file, setFile] = React.useState<UploadFile | null>(null);
    const [errors, setErrors] = React.useState<string[]>([]);

    const handleFileSelect = async (file: File) => {
        onErrorChange(false);
        setErrors([]);

        try {
            const uploadFile: UploadFile = {
                ...file,
                uid: Date.now().toString(),
            };
            setFile(uploadFile);
            const processResult = await processFile(file);

            if (processResult.success) {
                onStepChange("confirm");
            } else {
                setErrors(processResult.errors);
                onErrorChange(true);
            }
        } catch (error) {
            console.error("File processing error:", error);
            setFile(null);
            resetData();
            setErrors([
                error instanceof Error ? error.message : "Unknown error",
            ]);
            onErrorChange(true);
        }
    };

    const handleBack = () => {
        onStepChange("export");
    };

    const handleFileRemove = () => {
        setFile(null);
        resetData();
    };

    return (
        <div className={theme.stepContainer}>
            <h2>{t("ControlsBulkAssign_UploadStep_Title")}</h2>
            <Alert
                showIcon={true}
                type="info"
                message={t("ControlsBulkAssign_UploadStep_Instructions")}
                className={theme.infoAlertBox}
            />

            <Dragger
                accept=".xlsx"
                beforeUpload={(file) => {
                    handleFileSelect(file);
                    return false;
                }}
                fileList={file ? [file] : []}
                onRemove={handleFileRemove}
            >
                <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                    {t(
                        "ControlsBulkAssign_UploadStep_UploadContainerInstructions"
                    )}
                </p>
            </Dragger>

            {status === "error" && errors.length > 0 && (
                <ValidationErrors errors={errors} />
            )}
            <div className={theme.buttonGroup}>
                <Button onClick={handleBack}>{t("Back")}</Button>
            </div>
        </div>
    );
}
